import React, {useEffect, useRef, useState} from 'react';
import {withSitecoreContext} from '@sitecore-jss/sitecore-jss-react';
import styled from 'styled-components';
import Button from './components/shared/Button/Button';
import DogImage from './assets/images/duke.png';
import FourOhFourImage from './assets/images/404.svg';
import translate from './helpers/translate';
import { isServer } from '@sitecore-jss/sitecore-jss/utils';
import {useLocation} from 'react-router-dom';
import GlobalStyle from './style/global/GlobalStyles';
import MainNav from './components/MainNav/MainNav';
import Footer from './components/Footer/Footer';
import SearchBox from './components/SearchBox/SearchBox';
import UtilityNav from "./components/UtilityNav/UtilityNav.jsx";
const packageConfig = require('../package.json');
import { useTranslation } from 'react-i18next';
import { useFallbackGlobalContent } from './hooks';

const Container = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
    background-color: ${({theme}) => theme.colours.tan};

    > div {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: ${({theme}) => `${theme.spacing(70)} ${theme.spacing(30)} 0 `};
    }

    h1 {
        margin: 0;
        color: ${({theme}) => theme.colours.red};
        font-size: ${({theme}) => theme.spacing(90)};
        font-family: ${({theme}) => theme.typography.fontFamily.heading};
        font-family: "apercu pro", apercu pro, sans-serif;
        font-weight: 900;
        line-height: 1;
    }

    h3 {
        line-height: 2;
    }

    p {
        margin-bottom: ${({theme}) => theme.spacing(30)};
        color: ${({theme}) => theme.colours.adaBrown};
        font-family: ${({theme}) => theme.typography.fontFamily.main};
    }

    a {
        text-decoration: none;
    }

    img {
        max-width: 100%;
        margin-top: ${({theme}) => theme.spacing(20)};

        ${({theme}) => theme.breakPoints.minBp('desktop')} {
            margin-top: ${({theme}) => theme.spacing(65)};
        }
    }
	.reg-cont {
		flex-flow: column;
		align-content: center;
		justify-content: center;
		align-items: center;
	}
`;

// Renders a route-not-found message when no route is available from Sitecore
// The JSS equivalent of a 404 Not Found page.

// This is invoked from RouteHandler when Sitecore returns no valid route data.
// The NotFound component receives the Layout Service Context data, but no route data.
// This can be used to power parts of your site, such as navigation, from LS context additions
// without losing the ability to render them on your 404 pages :)

const NotFound = ({route, context, scontext, sitecoreContext, updateSitecoreContext}) => {
    const {viewBag = {}} = context || {};
    const {navigation = {}, footer = {}} = context || {};
    const {mainNav = []} = navigation;
    const {footerContent = {}} = footer;
    const recipePagePath = 'bean-recipes';
    const pagePath = useLocation().pathname;
    const [searchTerm, setSearchTerm] = useState('');
    const [search, setSearch] = useState(false);
    const [noSearchValue, setNoSearchValue] = useState(false);
    const searchRef = useRef(null);
    const { t } = useTranslation();

    // A 404 arrives with no globalContent/site in the context, which leaves the nav
    // and footer empty. Re-fetch it from the /notfound item. See the hook for detail.
    useFallbackGlobalContent(sitecoreContext, updateSitecoreContext);

    const {site} = sitecoreContext;
    //console.log('sitecoreContext in not found template', sitecoreContext);
    // console.log('sitecoreContext', site);
    // console.log('not found', packageConfig.config.appName);


    const handleRedirectRouting = () => {
        if (!isServer()) {
            const splitPath = window.location.pathname.split('/');
            const regex = new RegExp(/^([a-z]{2}_[A-Z]{2})/);

            if (regex.test(splitPath[1])) {
                if (splitPath[2] === recipePagePath) {
                    return `/${splitPath[1]}/${splitPath[2]}/`;
                }
                return `/${splitPath[1]}/`;
            }
            return '/';
        }
    };

    const getPageType = () => {
        if (pagePath.includes(recipePagePath)) return 'recipe';

        return 'default';
    };

    const getRelevantMessaging = (pageType = 'default') => {
        const messaging = {
            default: {
                text: t(
                    'BushsBeans.Global.404.Text',
                    'Whoops. Looks like you went a little too fast for your skis here.'
                ),
                buttonText: t('BushsBeans.Global.404.VisitHomepage', 'Visit Homepage'),
            },
            recipe: {
                text: t(
                    'BushsBeans.Global.404.RecipeText',
                    'Whoops! Looks like something went wrong. Try again or select the See All Recipes option below.'
                ),
                buttonText: t('BushsBeans.Global.404.SeeAllRecipes', 'See All Recipes'),
            },
        };

        if (pageType in messaging) return messaging[pageType];

        return messaging.default;
    };

    const pageType = getPageType();

    useEffect(() => {
        const searchButton = document.getElementById('search-button');
        if (searchButton) {
            const svgElement = searchButton.querySelector('svg');
            if (svgElement) {
                svgElement.style.display = 'none';
            }
        }
    }, []);
    // console.log('updatedContext', updatedContext);
    switch (site && site.name) {
        case 'bushs-beans-foodservices':
            return (
                <React.Fragment>
                    <GlobalStyle/>
                    <UtilityNav search={search} setSearch={setSearch}/>
                    <MainNav />
                    <main className="container f-s" id="four-oh-four">
                        <Container className="inner">
                            <section aria-label="Search Container" className="search-on-p">
                                <SearchBox
                                    searchValue={searchTerm}
                                    showError={noSearchValue}
                                    onChange={(event) => {
                                        setSearchTerm(event);
                                        if (noSearchValue) setNoSearchValue(false);
                                    }}
                                    onSubmit={(event, inputValue) => {
                                        if (searchTerm.length > 0) {
                                            window.location.href = `/search/?type=all&searchTerm=${searchTerm}`;
                                            setSearch(false);
                                        } else {
                                            setNoSearchValue(true);
                                        }
                                    }}
                                    className="search-box"
                                />
                            </section>
                            <div>
                                <h1 className="blue text-center 404h1">
                                    <img width="200" src={FourOhFourImage} alt="404 Page Not Found"/>
                                </h1>
                                <h2 className="blue">Sorry we couldn&apos;t find this page.</h2>
                                <p>Search our site or explore some links below:</p>
                                <ul className="buttonUl foodservice">
                                    <li>
                                        <Button className="fourOfourButton" text="Back to Home" href="/" variant="yellowBlue"/>
                                    </li>
                                    <li>
                                        <Button className="fourOfourButton" text="Explore Recipes" href="/recipes" variant="yellowBlue"/>
                                    </li>
                                    <li>
                                        <Button className="fourOfourButton" text="Explore Products" href="/bulk-products" variant="yellowBlue"/>
                                    </li>
                                </ul>
                                <div className="hidden reg-cont">
                                    <p>{getRelevantMessaging(pageType)?.text}</p>
                                    <Button variant="yellowBlue" text={getRelevantMessaging(pageType)?.buttonText}
                                            href={handleRedirectRouting()}/>
                                </div>
                            </div>
                        </Container>

                        <Footer/>
                    </main>

                </React.Fragment>
            );
        default:
            return (
                <React.Fragment>
                    <GlobalStyle/>
                    <MainNav />
                    <main className="container" id="four-oh-four">
                        <Container className="inner">
                            <section aria-label="Search Container" className="search-on-p">
                                <SearchBox
                                    searchValue={searchTerm}
                                    showError={noSearchValue}
                                    onChange={(event) => {
                                        setSearchTerm(event);
                                        if (noSearchValue) setNoSearchValue(false);
                                    }}
                                    onSubmit={(event, inputValue) => {
                                        if (searchTerm.length > 0) {
                                            window.location.href = `/search/?type=all&searchTerm=${searchTerm}`;
                                            setSearch(false);
                                        } else {
                                            setNoSearchValue(true);
                                        }
                                    }}
                                    className="search-box"
                                />
                            </section>
                            <div>
                                <h1>{t( 'BushsBeans.Global.404.Title', '404')}</h1>
                                <h2>THIS PAGE IS NOWHERE TO BEAN FOUND</h2>
                                <h3>HERE ARE SOME OTHER PLACES TO SNIFF OUT</h3>
                                <ul className="buttonUl">
                                    <li><a href="/en_US/">Home Page</a></li>
                                    <li><a href="/en_US/products">All Products</a></li>
                                    <li><a href="/en_US/about-us">About Us</a></li>
                                    <li><a href="/en_US/bean-recipes">Bean Recipes</a></li>
                                </ul>
                                <div className="hidden reg-cont">
                                    <p>{getRelevantMessaging(pageType)?.text}</p>
                                    <Button text={getRelevantMessaging(pageType)?.buttonText}
                                            href={handleRedirectRouting()}/>
                                </div>
                                <img src={DogImage} alt="Duke"/>
                            </div>
                        </Container>
                        <Footer />
                    </main>

                </React.Fragment>
            );
    }
};

// `updatable` gives us `updateSitecoreContext`, used to restore globalContent on 404s.
export default withSitecoreContext({ updatable: true })(NotFound);